import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders, getOrderById, getProfile, updateOrderStatus } from '../api';
import { showError, showSuccess } from './AlertModal';

function useRequireLogin() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const currentUserRaw = localStorage.getItem('currentUser');
  const currentUser = useMemo(() => {
    try {
      return currentUserRaw ? JSON.parse(currentUserRaw) : null;
    } catch {
      return null;
    }
  }, [currentUserRaw]);

  useEffect(() => {
    if (!token || !currentUser) {
      showError('Please sign in to continue.');
      navigate('/', { replace: true });
    }
  }, [token, currentUser, navigate]);

  return { token, currentUser };
}

function Card({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1rem 1.2rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', color: '#111827', fontSize: '1rem' }}>{title}</h3>
      {children}
    </div>
  );
}

// ── Status badge config ───────────────────────────────────────
const STATUS_CONFIG = {
  pending:          { label: 'Pending',          bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  confirmed:        { label: 'Confirmed',         bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  processing:       { label: 'Processing',        bg: '#ede9fe', color: '#5b21b6', dot: '#8b5cf6' },
  out_for_delivery: { label: 'Out for Delivery',  bg: '#ffedd5', color: '#9a3412', dot: '#f97316' },
  delivered:        { label: 'Delivered',         bg: '#dcfce7', color: '#166534', dot: '#16a34a' },
  cancelled:        { label: 'Cancelled',         bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: cfg.bg, color: cfg.color,
      padding: '3px 10px', borderRadius: '20px',
      fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.02em'
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

// ── Single order card ─────────────────────────────────────────
function OrderCard({ order, isAdmin, onStatusUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const toggleExpand = async () => {
    if (!expanded && !detail) {
      setLoading(true);
      try {
        const res = await getOrderById(order.id);
        if (res.success) setDetail(res.order);
      } catch {}
      setLoading(false);
    }
    setExpanded(v => !v);
  };

  const handleStatusChange = async (newStatus) => {
    if (!isAdmin || statusLoading) return;
    
    setStatusLoading(true);
    try {
      const res = await updateOrderStatus({
        order_id: order.id,
        status: newStatus
      });
      
      if (res.success) {
        showSuccess(res.message || 'Order status updated successfully');
        onStatusUpdate(order.id, newStatus);
      } else {
        showError(res.message || 'Failed to update order status');
      }
    } catch (error) {
      showError('Failed to update order status. Please try again.');
    } finally {
      setStatusLoading(false);
    }
  };

  const paymentLabel = {
    cod: 'Cash on Delivery', upi: 'UPI',
    card: 'Card', netbanking: 'Net Banking', wallet: 'Wallet',
  }[order.payment_method] || order.payment_method?.toUpperCase();

  const date = order.created_at
    ? new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: '0', overflow: 'hidden', background: '#fff' }}>
      {/* Header row */}
      <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: isAdmin ? '2fr 1fr 1fr 1fr 80px' : '2fr 1fr 1fr 1fr 80px', gap: '12px', alignItems: 'center', background: '#fff' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>{order.order_number}</span>
          {date && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{date}</span>}
          {isAdmin && order.full_name && (
            <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>👤 {order.full_name}</span>
          )}
        </div>
        <div>
          {isAdmin ? (
            <select
              value={order.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={statusLoading}
              style={{
                background: statusLoading ? '#f3f4f6' : STATUS_CONFIG[order.status]?.bg || '#f3f4f6',
                color: statusLoading ? '#9ca3af' : STATUS_CONFIG[order.status]?.color || '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '20px',
                padding: '3px 8px',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: statusLoading ? 'not-allowed' : 'pointer',
                outline: 'none'
              }}
            >
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          ) : (
            <StatusBadge status={order.status} />
          )}
        </div>
        <div>
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{paymentLabel}</span>
        </div>
        <div>
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#15803d' }}>₹{Number(order.total_amount || 0).toFixed(2)}</span>
        </div>
        <div>
          <button
            onClick={toggleExpand}
            style={{ background: expanded ? '#f0fdf4' : '#fff', border: `1px solid ${expanded ? '#16a34a' : '#d1d5db'}`, borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', fontSize: '0.75rem', color: expanded ? '#16a34a' : '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {loading ? '...' : expanded ? '▲ Hide' : '▼ Details'}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: '16px', borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
          {loading && <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Loading items...</p>}
          {detail && (
            <>
              {/* Admin: Customer Info */}
              {isAdmin && (
                <div style={{ marginBottom: '14px', background: '#f0f9ff', borderRadius: '8px', padding: '12px 14px' }}>
                  <p style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#374151', marginBottom: '8px' }}>Customer Information</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.8125rem' }}>
                    <div><strong>Name:</strong> {detail.full_name}</div>
                    <div><strong>Email:</strong> {detail.email}</div>
                    <div><strong>Phone:</strong> {detail.phone}</div>
                    <div><strong>User ID:</strong> {detail.user_id || 'Guest'}</div>
                  </div>
                </div>
              )}

              {/* Items */}
              <div style={{ marginBottom: '14px' }}>
                <p style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#374151', marginBottom: '10px' }}>Items Ordered</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(detail.items || []).map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: '#f9fafb', borderRadius: '8px' }}>
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 48, height: 48, background: '#e5e7eb', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.2rem' }}>🛒</div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}{item.unit ? ` (${item.unit})` : ''}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>{item.category}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#6b7280' }}>Qty: {item.quantity} × ₹{Number(item.price).toFixed(2)}</p>
                      </div>
                      <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#111827', flexShrink: 0 }}>₹{Number(item.subtotal).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price summary */}
              <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#6b7280', marginBottom: '5px' }}>
                  <span>Subtotal</span><span>₹{Number(detail.subtotal || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#6b7280', marginBottom: '5px' }}>
                  <span>Delivery</span>
                  <span style={{ color: Number(detail.delivery_fee) > 0 ? '#374151' : '#16a34a', fontWeight: 600 }}>
                    {Number(detail.delivery_fee) > 0 ? `₹${Number(detail.delivery_fee).toFixed(2)}` : 'FREE'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '0.9375rem', color: '#15803d', borderTop: '1px solid #bbf7d0', paddingTop: '8px', marginTop: '4px' }}>
                  <span>Grand Total</span><span>₹{Number(detail.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Delivery address */}
              <div style={{ fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.6 }}>
                <p style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Delivery Address</p>
                <p style={{ margin: 0 }}>{detail.address}, {detail.city} – {detail.pincode}</p>
                {detail.delivery_area && <p style={{ margin: 0 }}>📍 {detail.delivery_area}</p>}
                <p style={{ margin: '2px 0 0' }}>📞 {detail.phone}</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function MyOrdersPage() {
  const { token, currentUser } = useRequireLogin();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders]   = useState([]);
  const [error, setError]     = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Check if user is admin by fetching profile
        const profileData = await getProfile();
        if (active && profileData.success && profileData.user) {
          setIsAdmin(profileData.user.role === 'admin');
        }

        const data = await getOrders();
        if (!active) return;
        if (data.success) {
          setOrders(data.orders || []);
        } else {
          setError(data.message || 'Unable to fetch orders.');
        }
      } catch {
        if (active) setError('Unable to fetch orders.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleStatusUpdate = (orderId, newStatus) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      )
    );
  };

  return (
    <main style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Page header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#111827' }}>
              My Orders
              {isAdmin && <span style={{ marginLeft: '8px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 700 }}>ADMIN</span>}
            </h1>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
              {loading ? 'Loading...' : orders.length > 0 ? `${orders.length} order${orders.length > 1 ? 's' : ''} placed` : 'No orders yet'}
              {isAdmin && !loading && <span style={{ marginLeft: '8px', color: '#92400e' }}>• Admin can change order status</span>}
            </p>
          </div>
          {!loading && orders.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                const count = orders.filter(o => o.status === key).length;
                if (!count) return null;
                return (
                  <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: cfg.bg, color: cfg.color, padding: '4px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot }} />
                    {cfg.label} ({count})
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Table header */}
      {!loading && !error && orders.length > 0 && (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '2fr 1fr 1fr 1fr 80px' : '2fr 1fr 1fr 1fr 80px', gap: '12px', padding: '12px 16px', marginTop: '1.5rem', background: '#f3f4f6', borderRadius: '8px 8px 0 0', borderBottom: '2px solid #e5e7eb' }}>
            {(isAdmin ? ['Order ID / Customer', 'Status', 'Payment', 'Total', ''] : ['Order ID', 'Status', 'Payment', 'Total', '']).map(h => (
              <span key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 1.5rem ${orders.length > 0 ? '0' : '3rem'}` }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#9ca3af' }}>
            <div style={{ width: 36, height: 36, border: '3px solid #16a34a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            Loading your orders...
          </div>
        )}
        {!loading && error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '1rem', color: '#dc2626', fontSize: '0.875rem', marginTop: '1.5rem' }}>{error}</div>
        )}
        {!loading && !error && orders.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#9ca3af' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '12px' }}>📦</div>
            <p style={{ fontWeight: 700, color: '#374151', fontSize: '1rem', marginBottom: '4px' }}>No orders yet</p>
            <p style={{ fontSize: '0.85rem' }}>Your placed orders will appear here.</p>
          </div>
        )}
        {!loading && !error && orders.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden', marginBottom: '2.5rem' }}>
            {orders.map((order, idx) => (
              <div key={order.id} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                <OrderCard 
                  order={order} 
                  isAdmin={isAdmin}
                  onStatusUpdate={handleStatusUpdate}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}

export function MyAddressesPage() {
  const { currentUser } = useRequireLogin();
  const userLocationRaw = localStorage.getItem('userLocation');
  const userLocation = (() => {
    try { return userLocationRaw ? JSON.parse(userLocationRaw) : null; }
    catch { return null; }
  })();

  const addresses = userLocation ? [userLocation] : [];

  return (
    <main style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#111827' }}>Saved Addresses</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
            {addresses.length > 0 ? `${addresses.length} address saved` : 'No addresses saved'}
          </p>
        </div>
      </div>

      {/* Table header */}
      {addresses.length > 0 && (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', padding: '12px 16px', marginTop: '1.5rem', background: '#f3f4f6', borderRadius: '8px 8px 0 0', borderBottom: '2px solid #e5e7eb' }}>
            {['Address', 'City', 'Pincode'].map(h => (
              <span key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem 3rem' }}>
        {addresses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#9ca3af' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '12px' }}>📍</div>
            <p style={{ fontWeight: 700, color: '#374151', fontSize: '1rem', marginBottom: '4px' }}>No saved addresses</p>
            <p style={{ fontSize: '0.85rem' }}>Your registration address will appear here.</p>
          </div>
        ) : (
          <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden', background: '#fff', marginBottom: '2.5rem' }}>
            {addresses.map((addr, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', padding: '16px', borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: 36, height: 36, background: '#f0fdf4', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1rem' }}>📍</div>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>{addr.address || '—'}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>Registration address</p>
                  </div>
                </div>
                <span style={{ fontSize: '0.875rem', color: '#374151' }}>{addr.city || 'Hyderabad'}</span>
                <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: 600 }}>{addr.pincode || '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

export function MyProfilePage() {
  const { token, currentUser } = useRequireLogin();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(currentUser || null);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getProfile();
        if (!active) return;
        if (data.success && data.user) {
          setProfile(data.user);
          localStorage.setItem('currentUser', JSON.stringify(data.user));
        } else {
          setError(data.message || 'Unable to fetch profile.');
        }
      } catch {
        if (active) setError('Unable to fetch profile.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const fields = profile ? [
    { label: 'Full Name',   value: profile.full_name || profile.name || '—', icon: '👤' },
    { label: 'Email',       value: profile.email     || '—',                  icon: '✉️' },
    { label: 'Phone',       value: profile.phone     || '—',                  icon: '📞' },
    { label: 'Account Type',value: profile.user_type ? profile.user_type.toUpperCase() : (profile.role || '—'), icon: '🏷️' },
    { label: 'Member Since',value: profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—', icon: '📅' },
  ] : [];

  return (
    <main style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem', display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Avatar */}
          <div style={{ width: 52, height: 52, background: 'linear-gradient(135deg, #16a34a, #15803d)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(22,163,74,0.3)' }}>
            <span style={{ color: 'white', fontWeight: 800, fontSize: '1.3rem' }}>
              {(profile?.full_name || profile?.name || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#111827' }}>
              {profile?.full_name || profile?.name || 'My Profile'}
            </h1>
            <p style={{ margin: '3px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>{profile?.email || ''}</p>
          </div>
        </div>
      </div>

      {/* Table header */}
      {!loading && !error && profile && (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 2fr', gap: '12px', padding: '12px 16px', marginTop: '1.5rem', background: '#f3f4f6', borderRadius: '8px 8px 0 0', borderBottom: '2px solid #e5e7eb' }}>
            {['', 'Field', 'Value'].map(h => (
              <span key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</span>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem 3rem' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#9ca3af' }}>
            <div style={{ width: 36, height: 36, border: '3px solid #16a34a', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            Loading profile...
          </div>
        )}
        {!loading && error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '1rem', color: '#dc2626', fontSize: '0.875rem', marginTop: '1.5rem' }}>{error}</div>
        )}
        {!loading && !error && profile && (
          <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden', background: '#fff', marginBottom: '2.5rem' }}>
            {fields.map((field, idx) => (
              <div key={field.label} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 2fr', gap: '12px', padding: '15px 16px', borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none', alignItems: 'center' }}>
                <span style={{ fontSize: '1.1rem' }}>{field.icon}</span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#6b7280' }}>{field.label}</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>{field.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}
